// utils independent from react. Can be used in scripts

import { ImageSrc, LanguageKeys, LockMeasurements } from './CommonTypes';
import { localization } from './localization';


export const hasFlag = (val: LockMeasurements, flag: LockMeasurements) : boolean => (val & flag) !== LockMeasurements.None;
export const addFlag = (val: LockMeasurements, flag: LockMeasurements) : LockMeasurements => val | flag;
export const removeFlag = (val: LockMeasurements, flag: LockMeasurements): LockMeasurements => val & ~flag;



export const nonNull = <T>(val: T | null): val is T => {
  return val !== null;
};

export const getPathSegments = (path: string) => path.split(/[\\/]/g);
export const getBaseName = (path: string) => getPathSegments(path).pop()!;
export const getPrentDirPath = (path: string) => path.slice(0, -getBaseName(path).length - 1);

export const getFileExt = (filePath: string) => {
  const fileName = getBaseName(filePath);
  if(filePath.includes('.'))
    return '.' + fileName.split('.').pop();
  return '';
};

export const getNameWithoutExt = (path: string) => {
  const base = getBaseName(path);
  return base.slice(0, -getFileExt(base).length);
};

export const normalizeAngle = (angle: number) => {
  angle *= 180 / Math.PI;
  if (angle < -90) {
    return { angle: angle + 180, flipped: true };
  }
  if (angle > 90) {
    return { angle: angle - 180, flipped: true };
  }
  return { angle, flipped: false }
}

export const indexOfMax = (arr: number[]) =>
  arr.reduce((iMax, val, i, arr) => val > arr[iMax] ? i : iMax, 0);


export const getLengthString = (length: number, scale: number) => (length * (scale)).toFixed(2);

export function setAtIndex<TItem>(
  setter: React.Dispatch<React.SetStateAction<TItem[]>>,
  index: number,
  setItem: (prev: TItem | null) => TItem
) {
  setter(prev => {
    if (index >= prev.length) { // add item
        return [...prev, setItem(null)];
    } else { // alter
      return prev.map((elem, i) => i === index ? setItem(elem) : elem);
    }
  });
}

export function removeAtIndex<TItem>(
  setter: React.Dispatch<React.SetStateAction<TItem[]>>,
  index: number
) {
  setter(prev => [...prev.slice(0, index), ...prev.slice(index + 1)]);
}

export function fromBack<TItem>(arr: TItem[], index = 0) {
  return arr[arr.length - 1 - index];
}

export const toBlob = (width: number, height: number, type: string, ...images: {
  img: HTMLImageElement
  srcRect?: { x?: number, y?: number, width?: number, height?: number },
  destRect?: { x?: number, y?: number, width?: number, height?: number }
}[]) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  for (const { img, srcRect, destRect } of images) {
    ctx!.filter = img.style.filter || 'none';
    const w = srcRect?.width || img.width;
    const h = srcRect?.height || img.height;
    ctx?.drawImage(img, srcRect?.x || 0, srcRect?.y || 0, w, h, destRect?.x || 0, destRect?.y || 0, destRect?.width || w, destRect?.height || h);
  }
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) {
        resolve(blob);
      } else {
        reject("Couldn't create Blob");
      }
    }, type);
  });
};

export const svgToImg = (svg: string | Blob) => {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const svgBlob = typeof svg === 'string' ? new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }) : svg;
    const svgUrl = window.URL.createObjectURL(svgBlob);
    const svgImg = new Image();
    svgImg.onload = () => {
      window.URL.revokeObjectURL(svgUrl);
      resolve(svgImg);
    };
    svgImg.onerror = e => {
      window.URL.revokeObjectURL(svgUrl);
      if(typeof svg === 'string') {
        // fallback for safari
        svgImg.onload = () => resolve(svgImg);
        svgImg.onerror = e => reject(e);
        svgImg.src = 'data:image/svg+xml,' + svg;
      } else {
        reject(e);
      }
    }
    svgImg.src = svgUrl;
  });
};

export const extractPhenomExif = (dataStr: string) => {
  // search directly without bothering with exif
  const FeiStart = '<FeiImage';
  const FeiEnd = '</FeiImage>';
  const start = dataStr.indexOf(FeiStart);
  const end = dataStr.indexOf(FeiEnd, start);
  if (start >= 0 && end >= 0) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(dataStr.substring(start, end + FeiEnd.length), "text/xml");
    return doc;
  }
};

export const getPhenomExif = (blob: Blob) => {
  return new Promise<Document>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const doc = extractPhenomExif(e.target!.result as string);
      if (doc)
        resolve(doc)
      else
        reject("No Phenom Exif found");
    };
    reader.onerror = e => reject('Error reading blob');
    reader.readAsText(blob);
  });
};

export const getInfoFromName = (name: string) => {
  try {
    const reg = /.*_([0-9.]*)([num]{1,2})(\..*)?/;
    const match = reg.exec(name);
  
    return { pixel: parseFloat(match![1]), unit: match![2].replace('um', 'μm') };
  } catch {
    return undefined;
  }
};

export const getInfoFromExif = (exif: Document) => {
  try {
    const ph = exif.getElementsByTagName('pixelHeight')[0];
    const unit = ph.getAttribute('unit')?.replace('um', 'µm')!;
    const pixel = parseFloat(ph.innerHTML);

    return { unit, pixel };
  } catch {
    return undefined;
  }
};

export const getInfoOrDefault = async (name: string, blob: Blob, lang: LanguageKeys) => {
  try {
    let info = getInfoFromName(name);
    if (!info)
      info = getInfoFromExif(await getPhenomExif(blob));
    return info || { unit: localization[lang].measurements.pixelUnit, pixel: 1 };
  } catch {
    return { unit: localization[lang].measurements.pixelUnit, pixel: 1}
  }
};

export const toDataUrl = (blob: Blob) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const url = e.target?.result as string;
      if (url) {
        resolve(url);
      } else {
        reject("Could't create object url");
      }
    };
    reader.readAsDataURL(blob);
  });
};

export const imgName = (img: ImageSrc, lang: LanguageKeys) => typeof img.name === 'string' ? img.name : img.name[lang];

export const setTimer = (tickCallback: () => void, tick: number, amount: number) => {
  const cancel = () => { amount = 0; };
  const invoke = () => {
    if (--amount >= 0) {
      tickCallback();
      setTimeout(invoke, tick);
    }
  };
  setTimeout(invoke, tick);

  return cancel;
};

export const fromEntries = <T = any>(entries: Iterable<readonly [PropertyKey, T]>): { [k: string]: T } => {
  if (Object.fromEntries !== undefined) {
    return Object.fromEntries(entries);
  } else {
    let obj: any = {};
    for (const [k, v] of entries) {
      obj[k] = v;
    }
    return obj;
  }
};
